import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    ScreeningItem,
    ScreeningPscInspection,
    ScreeningComplianceRisk,
    ScreeningIncidents
} from "../types/Screening";
import { AppThunk, RootState } from "../redux/store";
import {
    getMainShipImage,
    getScreeningData,
    getScreeningPscData,
    getScreeningComplianceRisk,
    getScreeningIncidents
} from "../services/ScreeningService";
import { createParametricSelectorHook } from "../redux/hooks";
import { userSliceSelector } from "./userSlice";

export interface ScreeningState {
    data: ScreeningItem[];
    mainShipImage: string;
    loading: boolean;
    error: boolean;
    pscLoading: boolean;
    pscError: boolean;
    pscInspections: ScreeningPscInspection[];
    complianceRiskLoading: boolean;
    complianceRiskError: boolean;
    complianceRiskData: Record<string, ScreeningComplianceRisk>;
    incidentsLoading: boolean;
    incidentsError: boolean;
    incidentsData: Record<string, ScreeningIncidents>;
}

export const initialState: ScreeningState = {
    data: [],
    mainShipImage: "",
    loading: true,
    error: false,
    pscLoading: true,
    pscError: false,
    pscInspections: [],
    complianceRiskLoading: false,
    complianceRiskError: false,
    complianceRiskData: {},
    incidentsLoading: false,
    incidentsError: false,
    incidentsData: {}
};

export const screeningSlice = createSlice({
    name: "screening",
    initialState,
    reducers: {
        addData: (state, action: PayloadAction<ScreeningItem>) => {
            state.data = state.data.concat([action.payload]);
        },
        setImage: (state, action: PayloadAction<string>) => {
            state.mainShipImage = action.payload;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        setError: (state, action: PayloadAction<boolean>) => {
            state.error = action.payload;
        },
        setPscLoading: (state, action: PayloadAction<boolean>) => {
            state.pscLoading = action.payload;
        },
        setPscError: (state, action: PayloadAction<boolean>) => {
            state.pscError = action.payload;
        },
        setPscInspections: (
            state,
            action: PayloadAction<ScreeningPscInspection[]>
        ) => {
            state.pscInspections = action.payload;
        },
        setComplianceRiskLoading: (state, action: PayloadAction<boolean>) => {
            state.complianceRiskLoading = action.payload;
        },
        setComplianceRiskError: (state, action: PayloadAction<boolean>) => {
            state.complianceRiskError = action.payload;
        },
        setComplianceRiskData: (
            state,
            action: PayloadAction<{
                imo: string;
                data: ScreeningComplianceRisk;
            }>
        ) => {
            state.complianceRiskData[action.payload.imo] = action.payload.data;
        },
        setIncidentsLoading: (state, action: PayloadAction<boolean>) => {
            state.incidentsLoading = action.payload;
        },
        setIncidentsError: (state, action: PayloadAction<boolean>) => {
            state.incidentsError = action.payload;
        },
        setIncidentsData: (
            state,
            action: PayloadAction<{ imo: string; data: ScreeningIncidents }>
        ) => {
            state.incidentsData[action.payload.imo] = action.payload.data;
        }
    }
});

export const {
    addData,
    setImage,
    setLoading,
    setError,
    setPscLoading,
    setPscError,
    setPscInspections,
    setComplianceRiskLoading,
    setComplianceRiskError,
    setComplianceRiskData,
    setIncidentsLoading,
    setIncidentsError,
    setIncidentsData
} = screeningSlice.actions;

export default screeningSlice.reducer;

export const fetchScreeningData =
    (imo: string): AppThunk<Promise<boolean>> =>
    async (dispatch) => {
        dispatch(setLoading(true));

        try {
            const response = await getScreeningData([imo]);

            if (response?.length) {
                dispatch(addData(response[0]));
            } else {
                throw new Error("no data");
            }
        } catch (e) {
            console.error(e);
            dispatch(setError(true));

            return false;
        }

        dispatch(setLoading(false));
        return true;
    };

export const fetchMainShipImage =
    (imo: string): AppThunk =>
    async (dispatch) => {
        try {
            const response = await getMainShipImage(imo);

            if (response?.length) {
                dispatch(setImage(response));
            } else {
                throw new Error("no image");
            }
        } catch (e) {
            console.error(e);
        }

        dispatch(setLoading(false));
    };

export const fetchScreeningPscData =
    (imo: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setPscLoading(true));

        const { UserToken } = userSliceSelector(getState());

        try {
            const response = await getScreeningPscData(imo, UserToken);

            if (response) {
                dispatch(setPscInspections(response.inspections));
                // set others here like defiencies
            }
        } catch (e) {
            console.error(e);
            dispatch(setPscError(true));
        }

        dispatch(setPscLoading(false));
    };

export const selectScreening = (state: RootState) => state.Screening;

export const selectScreeningLoading = (state: RootState) =>
    state.Screening.loading;

export const selectScreeningError = (state: RootState) => state.Screening.error;

export const selectScreeningByIMO = createParametricSelectorHook(
    createSelector(
        [
            (state: RootState) => state.Screening.data,
            (_, imo: string | undefined) => imo
        ],
        (screeningData, imo) => {
            if (imo) {
                return screeningData.find(
                    (screening) => screening.vessel_imo === Number(imo)
                );
            }
        }
    )
);

export const selectMainShipImage = (state: RootState) =>
    state.Screening.mainShipImage;

export const fetchScreeningComplianceRisk =
    (imo: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setComplianceRiskLoading(true));

        const { UserToken } = userSliceSelector(getState());

        try {
            const response = await getScreeningComplianceRisk(imo, UserToken);

            if (response) {
                dispatch(setComplianceRiskData({ imo, data: response }));
            } else {
                throw new Error("no compliance risk data");
            }
        } catch (e) {
            console.error(e);
            dispatch(setComplianceRiskError(true));
        }

        dispatch(setComplianceRiskLoading(false));
    };

export const selectComplianceRiskByIMO = createParametricSelectorHook(
    createSelector(
        [
            (state: RootState) => state.Screening.complianceRiskData,
            (_, imo: string | undefined) => imo
        ],
        (complianceRiskData, imo) => {
            if (imo) {
                return complianceRiskData[imo];
            }
        }
    )
);

export const selectComplianceRiskLoading = (state: RootState) =>
    state.Screening.complianceRiskLoading;

export const fetchScreeningIncidents =
    (imo: string): AppThunk =>
    async (dispatch, getState) => {
        dispatch(setIncidentsLoading(true));

        const { UserToken } = userSliceSelector(getState());

        try {
            const response = await getScreeningIncidents(imo, UserToken);

            if (response) {
                dispatch(setIncidentsData({ imo, data: response }));
            }
        } catch (e) {
            console.error(e);
            dispatch(setIncidentsError(true));
        }

        dispatch(setIncidentsLoading(false));
    };

export const selectIncidentsByIMO = createParametricSelectorHook(
    createSelector(
        [
            (state: RootState) => state.Screening.incidentsData,
            (_, imo: string | undefined) => imo
        ],
        (incidentsData, imo) => {
            if (imo) {
                return incidentsData[imo];
            }
        }
    )
);

export const selectIncidentsLoading = (state: RootState) =>
    state.Screening.incidentsLoading;

export const selectIncidentsError = (state: RootState) =>
    state.Screening.incidentsError;

export const fetchScreening =
    (imo: string): AppThunk =>
    async (dispatch) => {
        const result = await dispatch(fetchScreeningData(imo));

        if (result) {
            dispatch(fetchScreeningPscData(imo));
            dispatch(fetchMainShipImage(imo));
            dispatch(fetchScreeningComplianceRisk(imo));
            dispatch(fetchScreeningIncidents(imo));
        }
    };
